import React, { useState, useRef, useMemo } from "react";
import { TextField, SortMenu, Select, Button } from "@ann/web-components";
import {
  FiltersContainer,
  FiltersLeftContent,
  FiltersRightContent,
  SearchContainer,
} from "./Filters.styled";

const Filters = ({
  onSortChange,
  onOrderChange,
  selectedSortOption = "timestamp",
  orderDirection = "desc",
  onSearchChange,
  onRoleChange,
  onActionChange,
  onSectionChange,
  onModifiedChange,
  loading = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [filtersKey, setFiltersKey] = useState(0);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedModified, setSelectedModified] = useState(null);
  const [actionSearchTerm, setActionSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for 500ms
    searchTimeoutRef.current = setTimeout(() => {
      onSearchChange?.(value);
    }, 500);
  };

  const handleRoleChange = (value, option) => {
    setSelectedRole(option);
    onRoleChange?.(value);
  };

  const handleActionChange = (value, option) => {
    setSelectedAction(option);
    onActionChange?.(value);
  };

  const handleSectionChange = (value, option) => {
    setSelectedSection(option);
    onSectionChange?.(value);
  };

  const handleModifiedChange = (value, option) => {
    setSelectedModified(option);
    onModifiedChange?.(value);
  };

  const handleActionSearch = (searchValue) => {
    setActionSearchTerm(searchValue);
  };

  const handleClearFilters = () => {
    // Clear all filter states (Role filter commented out)
    // setSelectedRole(null);
    setSelectedAction(null);
    setSelectedSection(null);
    setSelectedModified(null);
    
    // Increment filtersKey to force re-render of Select components
    setFiltersKey((prev) => prev + 1);
    
    // Call all onChange callbacks to update parent state
    // onRoleChange?.(null);
    onActionChange?.(null);
    onSectionChange?.(null);
    onModifiedChange?.(null);
  };

  // Check if any filters are active (Role filter commented out)
  const hasActiveFilters = useMemo(() => {
    return !!(
      // selectedRole ||
      selectedAction ||
      selectedSection ||
      selectedModified
    );
  }, [selectedAction, selectedSection, selectedModified]);

  // Role filter commented out - not available in audit endpoint response
  // const roleOptions = [
  //   { label: "Admin", value: "Admin" },
  //   { label: "User", value: "User" },
  //   { label: "Owner", value: "Owner" },
  // ];

  // Action options based on audit endpoint response
  const actionOptions = [
    { label: "Updated", value: "Updated" },
    { label: "Login", value: "Login" },
    { label: "Created", value: "Created" },
    { label: "Deleted", value: "Deleted" },
    { label: "Modified", value: "Modified" },
    { label: "Access", value: "Access" },
  ];

  // Section options based on audit endpoint response (uppercase format)
  const sectionOptions = [
    { label: "Contacts", value: "CONTACTS" },
    { label: "Auth", value: "AUTH" },
    { label: "Agents", value: "AGENTS" },
    { label: "Drives", value: "DRIVES" },
    { label: "Data", value: "DATA" },
    { label: "Conversations", value: "CONVERSATIONS" },
    { label: "Users", value: "USERS" },
    { label: "Workspace", value: "WORKSPACE" },
  ];

  const modifiedOptions = [
    { label: "Last 24 hours", value: "Last 24 hours" },
    { label: "Last 7 days", value: "Last 7 days" },
    { label: "Last 30 days", value: "Last 30 days" },
    { label: "Last 3 months", value: "Last 3 months" },
    { label: "Last year", value: "Last year" },
  ];

  // Filtered options based on search term (only for Action filter)
  const filteredActionOptions = useMemo(() => {
    if (!actionSearchTerm.trim()) return actionOptions;
    const searchLower = actionSearchTerm.toLowerCase();
    return actionOptions.filter(action => 
      action.label.toLowerCase().includes(searchLower) ||
      action.value.toLowerCase().includes(searchLower)
    );
  }, [actionSearchTerm]);

  return (
    <FiltersContainer>
      <FiltersLeftContent>
        <SearchContainer>
          <TextField
            iconLeft="search"
            onChange={handleSearchChange}
            size="small"
            placeholder="Search"
            value={searchValue}
            disabled={loading}
          />
        </SearchContainer>

        {/* Role filter commented out - not available in audit endpoint response */}
        {/* <Select 
          key={`role-filter-${filtersKey}`}
          label="Role" 
          placeholder={selectedRole?.label || "Role"} 
          onChange={handleRoleChange}
          size="small"
          width="auto"
          minWidth="100px"
          disabled={loading}
        >
          <Select.Menu>
            {roleOptions.map((role) => (
              <Select.Item 
                key={role.value} 
                value={role.value} 
                label={role.label}
              >
                {role.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select> */}

        <Select 
          key={`action-filter-${filtersKey}`}
          label="Action" 
          placeholder={selectedAction?.label || "Action"} 
          onChange={handleActionChange}
          size="small"
          width="auto"
          minWidth="100px"
          disabled={loading}
        >
          <Select.Menu>
            <Select.Search 
              placeholder='Search actions...' 
              onChange={handleActionSearch}
              label=""
            />
            {filteredActionOptions.map((action) => (
              <Select.Item 
                key={action.value} 
                value={action.value} 
                label={action.label}
              >
                {action.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>

        <Select 
          key={`section-filter-${filtersKey}`}
          label="Section" 
          placeholder={selectedSection?.label || "Section"} 
          onChange={handleSectionChange}
          size="small"
          width="auto"
          minWidth="100px"
          disabled={loading}
        >
          <Select.Menu>
            {sectionOptions.map((section) => (
              <Select.Item 
                key={section.value} 
                value={section.value} 
                label={section.label}
              >
                {section.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>

        <Select 
          key={`modified-filter-${filtersKey}`}
          label="Modified" 
          placeholder={selectedModified?.label || "Modified"} 
          onChange={handleModifiedChange}
          size="small"
          width="auto"
          minWidth="140px"
          disabled={loading}
        >
          <Select.Menu>
            {modifiedOptions.map((modified) => (
              <Select.Item 
                key={modified.value} 
                value={modified.value} 
                label={modified.label}
              >
                {modified.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>

        {hasActiveFilters && (
          <Button
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearFilters}
            disabled={loading}
          />
        )}
      </FiltersLeftContent>

      <FiltersRightContent>
        <SortMenu
          minWidth="200px"
          onChange={(id) => {
            if (onSortChange) {
              onSortChange(id);
            }
          }}
          order={orderDirection ? orderDirection.toLowerCase() : "desc"}
          onOrderChange={(newOrder) => {
            if (onOrderChange) {
              onOrderChange(newOrder);
            }
          }}
          options={[
            {
              icon: 'clock',
              id: 'timestamp',
              label: 'Most Recent'
            },
            {
              icon: 'sortByAlpha',
              id: 'userName',
              label: 'User Name'
            },
            {
              icon: 'label',
              id: 'action',
              label: 'Action'
            },
            {
              icon: 'category',
              id: 'section',
              label: 'Section'
            }
          ]}
          selected={selectedSortOption || "timestamp"}
          size="medium"
          title="SORT BY"
          width="240px"
          disabled={loading}
        />
      </FiltersRightContent>
    </FiltersContainer>
  );
};

export default Filters;

